/** Set `VITE_GOOGLE_MAPS_API_KEY` in `.env`. */
export const GOOGLE_MAPS_API_KEY =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

export const GOOGLE_MAPS_LIBRARIES = ['geometry', 'drawing', 'places'];

export const DEFAULT_MAP_CENTER = { lat: 43.082857, lng: -89.3676184 };

const POI_TYPES = new Set([
  'establishment',
  'point_of_interest',
  'premise',
  'park',
  'museum',
  'tourist_attraction',
]);

export function parseCoord(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getAddressComponent(components, type) {
  const component = components?.find((c) => c.types?.includes(type));
  return component?.long_name ?? '';
}

export function parseAddressComponents(components) {
  const streetNumber = getAddressComponent(components, 'street_number');
  const route = getAddressComponent(components, 'route');
  const streetAddress =
    [streetNumber, route].filter(Boolean).join(' ').trim() || undefined;

  return {
    streetAddress,
    city:
      getAddressComponent(components, 'locality') ||
      getAddressComponent(components, 'sublocality') ||
      undefined,
    state:
      getAddressComponent(components, 'administrative_area_level_1') ||
      undefined,
    zipcode: getAddressComponent(components, 'postal_code') || undefined,
  };
}

export function placeResultToLocation(place) {
  const lat = place.geometry?.location?.lat?.();
  const lng = place.geometry?.location?.lng?.();
  if (lat == null || lng == null) {
    return null;
  }

  const addressParts = parseAddressComponents(place.address_components ?? []);

  return {
    latitude: lat,
    longitude: lng,
    locationName: place.name?.trim() || undefined,
    formattedAddress: place.formatted_address,
    ...addressParts,
  };
}

export function geocoderResultsToLocation(results, lat, lng) {
  if (!results?.length) {
    return { latitude: lat, longitude: lng };
  }

  const poiResult = results.find((r) =>
    r.types?.some((type) => POI_TYPES.has(type)),
  );
  const addressResult =
    results.find(
      (r) => r.types?.includes('street_address') || r.types?.includes('route'),
    ) ?? results[0];
  const primary = addressResult ?? results[0];

  const addressParts = parseAddressComponents(primary.address_components ?? []);

  let locationName;
  if (poiResult?.formatted_address) {
    locationName = poiResult.formatted_address.split(',')[0]?.trim();
  }

  return {
    latitude: lat,
    longitude: lng,
    locationName,
    formattedAddress: primary.formatted_address,
    ...addressParts,
  };
}

/** Human-readable label for the location text field. */
export function formatLocationDisplay(location) {
  if (!location) {
    return '';
  }

  const {
    locationName,
    formattedAddress,
    streetAddress,
    city,
    state,
    zipcode,
  } = location;
  const addressLine = [streetAddress, city, state, zipcode]
    .filter(Boolean)
    .join(', ');

  if (locationName && addressLine) {
    if (addressLine.toLowerCase().includes(locationName.toLowerCase())) {
      return addressLine;
    }
    return `${locationName}, ${addressLine}`;
  }

  return locationName || addressLine || formattedAddress || '';
}

function legacyReverseGeocode(lat, lng) {
  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.length) {
        resolve(geocoderResultsToLocation(results, lat, lng));
        return;
      }
      reject(new Error(status));
    });
  });
}

async function modernReverseGeocode(lat, lng) {
  const { Geocoder } = await google.maps.importLibrary('geocoding');
  const geocoder = new Geocoder();
  const response = await geocoder.geocode({ location: { lat, lng } });
  const results = response?.results;

  if (!results?.length) {
    throw new Error('ZERO_RESULTS');
  }

  return geocoderResultsToLocation(results, lat, lng);
}

/** Reverse-geocode map coordinates to a postal address. */
export async function reverseGeocode(lat, lng) {
  if (typeof google?.maps?.Geocoder === 'function') {
    try {
      const result = await legacyReverseGeocode(lat, lng);
      if (formatLocationDisplay(result)) {
        return result;
      }
    } catch {
      // Fall through to the modern Geocoder below.
    }
  }

  if (typeof google?.maps?.importLibrary === 'function') {
    const result = await modernReverseGeocode(lat, lng);
    if (formatLocationDisplay(result)) {
      return result;
    }
  }

  throw new Error('GEOCODE_FAILED');
}
